import { FC, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/hooks/redux';
import { useGetTotalUnreadNotificationsMutation, useGetHeaderNotificationsMutation } from 'app/redux/api/notification/notification.api';
import { setUnreadCount, setHeaderNotifications, setLoading } from 'app/redux/slices/notification/notification.slice';
import { WithChildren } from 'app/types/withChildren';

const NOTIFICATIONS_INTERVAL_FOCUSED = 30 * 1000; // 30s
const NOTIFICATIONS_INTERVAL_UNFOCUSED = 150 * 1000; // 2m 30s

export const PollingNotifications: FC<WithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [isVisible, setIsVisible] = useState(document.visibilityState === 'visible');
  const [isWindowFocused, setIsWindowFocused] = useState(() => document.hasFocus());
  
  const { currentUser } = useAppSelector(state => state.user);

  const [requestTotalUnreadNotifications] = useGetTotalUnreadNotificationsMutation();
  const [requestHeaderNotifications] = useGetHeaderNotificationsMutation();

  const fetchUnreadNotifications = () => {
    dispatch(setLoading(true));
    
    requestTotalUnreadNotifications()
      .unwrap()
      .then(count => {
        dispatch(setUnreadCount(count));
        
        // Also fetch header notifications when unread count changes
        return requestHeaderNotifications().unwrap();
      })
      .then(headerData => {
        dispatch(setHeaderNotifications({
          notifications: headerData.notifications,
          total: headerData.total
        }));
      })
      .catch(() => {
        // Silent fail for polling
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    if (!currentUser || !isVisible) {
      return;
    }

    // Initial fetch
    fetchUnreadNotifications();
    
    // Set up polling interval based on window focus
    const interval = setInterval(() => {
      fetchUnreadNotifications();
    }, isWindowFocused ? NOTIFICATIONS_INTERVAL_FOCUSED : NOTIFICATIONS_INTERVAL_UNFOCUSED);

    return () => clearInterval(interval);
  }, [currentUser, isVisible, isWindowFocused]);

  return (
    <>
      {children}
    </>
  );
}; 